import { Layout } from "components/layout/Layout";
import { useFeatureFlags } from "hooks/use-config";
import { usePostLoginRedirect } from "hooks/use-post-login-redirect";
import { useResetInteractionOnRouteChange } from "hooks/use-reset-interaction-on-route-change";
import { ChapterSubPageRoute } from "pages/circular-cmf-guide/ChapterSubPageRoute";
import { CircularCmfGuidePage } from "pages/circular-cmf-guide/CircularCmfGuidePage";
import { DiscoveryLayout } from "pages/discovery/DiscoveryLayout";
import { AccessDenied } from "pages/error/accessDenied/AccessDenied";
import { NotFound } from "pages/error/notFound/NotFound";
import { HelpPage } from "pages/help/HelpPage";
import { Onboarding } from "pages/onboarding/Onboarding";
import { ProfilePage } from "pages/profile/ProfilePage";
import { SearchPage } from "pages/search/SearchPage";
import { TrendCardDetailPage } from "pages/trend/TrendCardDetailPage";
import { TrendDetailPage } from "pages/trend/TrendDetailPage";
import { UserSettings } from "pages/user-settings/UserSettings";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import { type FC, useContext } from "react";
import { Route, Routes } from "react-router-dom";
import { DigitalSampleAssetDownloadPage } from "./pages/download/DigitalSampleAssetDownloadPage";
import { StartPage } from "./pages/start/StartPage";

export type AppRoutingProps = {};

export const AppRouting: FC<AppRoutingProps> = () => {
  const { settings, setSettings } =
    useContext<SettingsContextValue>(SettingsContext);
  const flags = useFeatureFlags();

  // CX-3744: resume a deferred CTA deep link (View in Imagio / Check it now)
  // after login. Placed here — inside the mounted <Routes>, past App's
  // app-config loader — so the in-app navigate lands on a live router instead
  // of being issued while the full-screen loader is still on screen.
  usePostLoginRedirect();

  // CX-3744: clear the interaction latch on navigation so the destination
  // page's on-mount bootstrap 401s stay silent (they are passive detection, not
  // a user-driven auth need) until the user interacts with that page.
  useResetInteractionOnRouteChange();

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route element={<DiscoveryLayout />}>
          <Route index element={<StartPage />} />
          <Route path="search" element={<SearchPage />} />
        </Route>
        <Route
          path="downloads/digital-sample/:digitalSampleId/assets/:assetId"
          element={<DigitalSampleAssetDownloadPage />}
        />
        <Route path="profile" element={<ProfilePage />} />
        <Route
          path="settings"
          element={<UserSettings settings={settings} onSave={setSettings} />}
        />
        {flags.trendPages && (
          <>
            <Route path="trend/:trendSlug" element={<TrendDetailPage />} />
            <Route
              path="trend/:trendSlug/:cardSlug"
              element={<TrendCardDetailPage />}
            />
          </>
        )}
        {flags.circularCmfGuide && (
          <>
            <Route
              path="circular-cmf-guide"
              element={<CircularCmfGuidePage />}
            />
            <Route
              path="circular-cmf-guide/:chapterSlug"
              element={<ChapterSubPageRoute />}
            />
          </>
        )}
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="help-center" element={<HelpPage />} />
        <Route path="access-denied" element={<AccessDenied />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};
